// @flow
import * as React from 'react';
import { TextInput, Stack, Box } from 'grommet';
import { defineMessages } from 'react-intl';
import * as Yup from 'yup';
import styled from 'styled-components';
import FieldWrapper from './FieldWrapper';
import StyledCloseButton from './StyledCloseButton';

const StyledBox = styled(Box)`
    position: absolute;
    right: 10px
`;

type Props = {
    field: {
        onFocus: (e: SyntheticFocusEvent<HTMLInputElement>) => void,
        onBlur: (e: SyntheticFocusEvent<any>) => void,
        onChange: (e: SyntheticInputEvent<HTMLInputElement>) => void,
        value: any,
        name: string
    },
    form: {
        isSubmitting: boolean,
        setFieldValue: (string, any) => void,
    },
    required: boolean,
    label: string,
    help_text: string,
    defaultValue: number,
    min_value: number,
    max_value: number,
    disabled?: boolean,
    step?: number,
}


const NumberInput = (props: Props): React.Node => {
    const {
        disabled,
        field: {
            name, onChange, onBlur, value,
        },
        form: { isSubmitting, setFieldValue },
        label,
        help_text: help,
        required,
        min_value: minValue,
        max_value: maxValue,
        defaultValue,
        step,
    } = props;

    let _value = value;

    if (typeof value === 'undefined' || value === null) {
        _value = (typeof defaultValue === 'undefined' || defaultValue === null) ? label : defaultValue;
    }

    React.useEffect(() => {
        if (value !== 'undefined' && minValue !== null && value < minValue) {
            setFieldValue(name, minValue);
        } else if (value !== 'undefined' && maxValue !== null && value > maxValue) {
            setFieldValue(name, maxValue);
        }
    }, [value, maxValue, minValue, name, setFieldValue]);

    const onClear = () => {
        setFieldValue(name, typeof defaultValue !== 'undefined' ? defaultValue : null);
    };

    return (
        <FieldWrapper
            name={name}
            required={required}
            helpText={help}
            label={label}
            placeholder=""
        >
            <Stack anchor="right">
                <TextInput
                    type="number"
                    name={name}
                    onChange={(e) => {
                        onChange(e);
                        setFieldValue(name, e.target.value);
                    }}
                    placeholder=""
                    onBlur={onBlur}
                    value={_value}
                    id={`id-${name}`}
                    disabled={disabled || isSubmitting}
                    min={minValue}
                    max={maxValue}
                    step={step}
                />
                {value && (
                    <StyledBox style={{ top: -16, right: 32 }}>
                        <StyledCloseButton onClick={onClear} plain />
                    </StyledBox>
                )}
            </Stack>

        </FieldWrapper>
    );
};

const messages = defineMessages({
    required: {
        id: 'numberinput-required',
        defaultMessage: 'This field is required.',
    },
    min: {
        id: 'numberinput-min',
        defaultMessage: 'The value must be greater than or equal to {minValue}!',
    },
    max: {
        id: 'numberinput-max',
        defaultMessage: 'The value must be less than or equal to {maxValue}!',
    },
});

NumberInput.getValidationSchema = (field: { validation: Object }) => {
    let errMsg = Yup.number(messages.invalid).nullable();
    if (field.validation.required) {
        errMsg = errMsg.required(messages.required).nullable();
    }
    if (typeof field.validation.min_value !== 'undefined' && field.validation.min_value !== null) {
        errMsg = errMsg.min(field.validation.min_value.toString(),
            { ...messages.min, values: { minValue: (field.validation.min_value).toString() } });
    }
    if (typeof field.validation.max_value !== 'undefined' && field.validation.max_value !== null) {
        errMsg = errMsg.max(field.validation.max_value.toString(),
            { ...messages.max, values: { maxValue: field.validation.max_value.toString() } });
    }

    return errMsg;
};

export default NumberInput;

NumberInput.defaultProps = {
    disabled: false,
    step: undefined,
};
